import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, MessageSquare, Users, TrendingUp, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";

const STATUS = {
  nouveau: { label: "Nouveau", cls: "bg-blue-100 text-blue-800" },
  en_cours: { label: "En cours", cls: "bg-amber-100 text-amber-800" },
  traite: { label: "Traité", cls: "bg-green-100 text-green-800" },
  archive: { label: "Archivé", cls: "bg-slate-100 text-slate-700" },
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [devis, setDevis] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);

  const load = async () => {
    try {
      const [s, d, c, cl] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/devis"),
        api.get("/contact"),
        api.get("/admin/clients"),
      ]);
      setStats(s.data);
      setDevis(d.data);
      setContacts(c.data);
      setClients(cl.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { load(); }, []);

  const updateDevisStatus = async (id, status) => {
    try {
      await api.patch(`/devis/${id}`, { status });
      toast.success("Statut mis à jour");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const updateContactStatus = async (id, status) => {
    try {
      await api.patch(`/contact/${id}`, { status });
      toast.success("Statut mis à jour");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-5rem)]">
      <section className="pt-14 pb-8 bg-white border-b border-slate-100">
        <div className="mx-auto max-w-7xl px-6 md:px-10 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#2196F3] mb-2">Administration</div>
            <h1 className="font-display text-3xl md:text-4xl font-extrabold text-slate-900">Tableau de bord</h1>
            <p className="text-slate-500 mt-1">Bienvenue {user?.name}. Voici l'activité de votre entreprise.</p>
          </div>
          <Button variant="outline" onClick={handleLogout} className="rounded-full" data-testid="admin-logout">
            <LogOut className="mr-2 h-4 w-4" /> Déconnexion
          </Button>
        </div>
      </section>

      <section className="py-10">
        <div className="mx-auto max-w-7xl px-6 md:px-10 space-y-8">
          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={FileText} label="Total devis" value={stats?.total_devis ?? "—"} accent />
            <StatCard icon={TrendingUp} label="Nouveaux devis" value={stats?.nouveau_devis ?? "—"} />
            <StatCard icon={MessageSquare} label="Messages contact" value={stats?.total_contacts ?? "—"} />
            <StatCard icon={Users} label="Clients inscrits" value={stats?.total_clients ?? "—"} />
          </div>

          <Tabs defaultValue="devis" className="space-y-6">
            <TabsList className="bg-white border border-slate-100 p-1 h-auto">
              <TabsTrigger value="devis" className="data-[state=active]:bg-[#2196F3] data-[state=active]:text-white px-5 py-2 rounded-md" data-testid="admin-tab-devis">Devis</TabsTrigger>
              <TabsTrigger value="contacts" className="data-[state=active]:bg-[#2196F3] data-[state=active]:text-white px-5 py-2 rounded-md" data-testid="admin-tab-contacts">Messages</TabsTrigger>
              <TabsTrigger value="clients" className="data-[state=active]:bg-[#2196F3] data-[state=active]:text-white px-5 py-2 rounded-md" data-testid="admin-tab-clients">Clients</TabsTrigger>
            </TabsList>

            <TabsContent value="devis">
              <div className="bg-white rounded-3xl border border-slate-100 p-6">
                <h2 className="font-display font-bold text-lg mb-5">Demandes de devis</h2>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Demandeur</TableHead>
                        <TableHead>Prestation</TableHead>
                        <TableHead>m²</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {devis.length === 0 && (
                        <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">Aucune demande</TableCell></TableRow>
                      )}
                      {devis.map((d) => {
                        const s = STATUS[d.status] || STATUS.nouveau;
                        return (
                          <TableRow key={d.id} data-testid={`admin-devis-row-${d.id}`}>
                            <TableCell className="text-sm">{new Date(d.created_at).toLocaleDateString("fr-FR")}</TableCell>
                            <TableCell>
                              <div className="font-medium">{d.full_name}</div>
                              <div className="text-xs text-slate-500">{d.email}</div>
                            </TableCell>
                            <TableCell className="text-sm">{d.service_type}</TableCell>
                            <TableCell>{d.surface_area}</TableCell>
                            <TableCell><Badge className={s.cls}>{s.label}</Badge></TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Dialog>
                                  <DialogTrigger asChild>
                                    <Button size="sm" variant="outline" onClick={() => setSelected(d)} data-testid={`admin-devis-view-${d.id}`}>Voir</Button>
                                  </DialogTrigger>
                                  <DialogContent className="max-w-lg">
                                    <DialogHeader>
                                      <DialogTitle>Demande — {selected?.full_name}</DialogTitle>
                                      <DialogDescription>Reçue le {selected && new Date(selected.created_at).toLocaleString("fr-FR")}</DialogDescription>
                                    </DialogHeader>
                                    {selected && (
                                      <div className="space-y-2 text-sm">
                                        <Row k="Email" v={selected.email} />
                                        <Row k="Téléphone" v={selected.phone} />
                                        <Row k="Société" v={selected.company || "—"} />
                                        <Row k="Adresse" v={selected.address} />
                                        <Row k="Prestation" v={selected.service_type} />
                                        <Row k="Superficie" v={`${selected.surface_area} m²`} />
                                        <Row k="Fréquence" v={selected.frequency} />
                                        <div className="pt-2 border-t">
                                          <div className="text-xs text-slate-500 mb-1">Message</div>
                                          <div className="text-slate-800 whitespace-pre-wrap">{selected.message || "—"}</div>
                                        </div>
                                      </div>
                                    )}
                                  </DialogContent>
                                </Dialog>
                                <Select value={d.status} onValueChange={(v) => updateDevisStatus(d.id, v)}>
                                  <SelectTrigger className="h-9 w-32" data-testid={`admin-devis-status-${d.id}`}><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="nouveau">Nouveau</SelectItem>
                                    <SelectItem value="en_cours">En cours</SelectItem>
                                    <SelectItem value="traite">Traité</SelectItem>
                                    <SelectItem value="archive">Archivé</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="contacts">
              <div className="bg-white rounded-3xl border border-slate-100 p-6">
                <h2 className="font-display font-bold text-lg mb-5">Messages de contact</h2>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Expéditeur</TableHead>
                        <TableHead>Sujet</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {contacts.length === 0 && (
                        <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-500">Aucun message</TableCell></TableRow>
                      )}
                      {contacts.map((c) => {
                        const s = STATUS[c.status] || STATUS.nouveau;
                        return (
                          <TableRow key={c.id}>
                            <TableCell className="text-sm">{new Date(c.created_at).toLocaleDateString("fr-FR")}</TableCell>
                            <TableCell>
                              <div className="font-medium">{c.full_name}</div>
                              <div className="text-xs text-slate-500">{c.email}</div>
                            </TableCell>
                            <TableCell className="text-sm">{c.subject}</TableCell>
                            <TableCell><Badge className={s.cls}>{s.label}</Badge></TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Dialog>
                                  <DialogTrigger asChild>
                                    <Button size="sm" variant="outline" onClick={() => setSelectedContact(c)}>Voir</Button>
                                  </DialogTrigger>
                                  <DialogContent className="max-w-lg">
                                    <DialogHeader>
                                      <DialogTitle>Message — {selectedContact?.full_name}</DialogTitle>
                                      <DialogDescription>Sujet : {selectedContact?.subject}</DialogDescription>
                                    </DialogHeader>
                                    {selectedContact && (
                                      <div className="space-y-2 text-sm">
                                        <Row k="Email" v={selectedContact.email} />
                                        <Row k="Téléphone" v={selectedContact.phone || "—"} />
                                        <div className="pt-2 border-t">
                                          <div className="text-xs text-slate-500 mb-1">Message</div>
                                          <div className="text-slate-800 whitespace-pre-wrap">{selectedContact.message}</div>
                                        </div>
                                      </div>
                                    )}
                                  </DialogContent>
                                </Dialog>
                                <Select value={c.status} onValueChange={(v) => updateContactStatus(c.id, v)}>
                                  <SelectTrigger className="h-9 w-32"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="nouveau">Nouveau</SelectItem>
                                    <SelectItem value="en_cours">En cours</SelectItem>
                                    <SelectItem value="traite">Traité</SelectItem>
                                    <SelectItem value="archive">Archivé</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="clients">
              <div className="bg-white rounded-3xl border border-slate-100 p-6">
                <h2 className="font-display font-bold text-lg mb-5">Clients inscrits</h2>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Inscription</TableHead>
                        <TableHead>Nom</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Téléphone</TableHead>
                        <TableHead>Société</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {clients.length === 0 && (
                        <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-500">Aucun client</TableCell></TableRow>
                      )}
                      {clients.map((c) => (
                        <TableRow key={c.id}>
                          <TableCell className="text-sm">{new Date(c.created_at).toLocaleDateString("fr-FR")}</TableCell>
                          <TableCell className="font-medium">{c.name}</TableCell>
                          <TableCell className="text-sm">{c.email}</TableCell>
                          <TableCell className="text-sm">{c.phone || "—"}</TableCell>
                          <TableCell className="text-sm">{c.company || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent = false }) {
  return (
    <div className={`rounded-2xl p-6 ${accent ? "bg-[#2196F3] text-white" : "bg-white border border-slate-100"}`}>
      <div className="flex items-center justify-between">
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${accent ? "bg-white/20" : "bg-[#E3F2FD]"}`}>
          <Icon className={`h-5 w-5 ${accent ? "text-white" : "text-[#2196F3]"}`} />
        </div>
      </div>
      <div className={`font-display font-extrabold text-3xl mt-4 ${accent ? "text-white" : "text-slate-900"}`}>{value}</div>
      <div className={`text-xs mt-1 uppercase tracking-wider ${accent ? "text-white/80" : "text-slate-500"}`}>{label}</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-4">
      <div className="text-slate-500">{k}</div>
      <div className="text-slate-900 font-medium text-right">{v}</div>
    </div>
  );
}
